import { ReactElement, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useToggle } from 'react-use';

import { AddressInformation } from '@frontend/api';
import {
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  CreatableSelect,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  PencilBarIcon,
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverButtonGroup,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  Tooltip,
} from '@frontend/components';

import FacilityDrawer from 'modules/common/drawers/facilityDrawer/FacilityDrawer';
import { open as openFacilityDrawer } from 'modules/common/drawers/facilityDrawer/data/actions';
import { MODES as FACILITY_DRAWER_MODES } from 'modules/common/drawers/facilityDrawer/data/constants';
import { SuggestedAddressModal } from 'modules/common/modals/suggestedAddressModal';

import { useAppSelector } from 'store/hooks';

import { formatAddressString } from 'utils/formatters';

import {
  selectNewlyAddedFacility,
  setFacility,
  setShippingInformation,
  setShippingType,
} from '../data/actions';
import { useConfirmAddressState } from '../data/hooks';
import {
  getAccount,
  getContact,
  getFacilities,
  getFacility,
  getShippingInformation,
} from '../data/reducer';
import { Facility } from '../data/types';
import { convertFacilityToShippingInformation } from '../data/utils';
import { AddressDisplay, InlinedAddressDisplay } from './AddressDisplay';
import {
  OneTimeAddressForm,
  useOneTimeAddressForm,
} from './OneTimeAddressForm';

type FacilityOption = {
  value: number;
  label: ReactElement;
};

const getFacilityOption = (facility: Facility) => {
  const addressInformation = {
    attention: facility.attention,
    businessName: facility.name,
    address: facility.address,
  };
  return {
    label: (
      <AddressDisplay
        addressInformation={addressInformation}
        color="feature.secondary"
      />
    ),
    value: facility.id,
  };
};

const getFacilityValue = (facility: Facility) => {
  const addressInformation = {
    attention: facility.attention,
    businessName: facility.name,
    address: facility.address,
  };
  return {
    label: (
      <InlinedAddressDisplay
        addressInformation={addressInformation}
        color="feature.secondary"
        spanProps={{ fontWeight: 'heavy' }}
      />
    ),
    value: facility.id,
  };
};

export const ShipToFacility = () => {
  const dispatch = useDispatch();
  const shippingInformation = useAppSelector(getShippingInformation) ?? null;
  const facilities = useAppSelector(getFacilities);
  const contact = useAppSelector(getContact);
  const account = useAppSelector(getAccount);
  const facility = useAppSelector(getFacility);

  const facilitiesById = useMemo(() => {
    return Object.fromEntries(
      facilities.map((facility) => [facility.id, facility]),
    );
  }, [facilities]);

  const canSelectFacility = Boolean(account);

  const oneTimeAddress = useMemo(() => {
    return shippingInformation && !facility ? shippingInformation : null;
  }, [shippingInformation, facility]);

  const [useOneTimeAddress, toggleUseOneTimeAddress] = useToggle(
    Boolean(oneTimeAddress) || !canSelectFacility,
  );

  const [showOneTimeAddressForm, toggleShowOneTimeAddressForm] = useToggle(
    useOneTimeAddress && !oneTimeAddress,
  );

  const facilityOptions = useMemo(
    () => facilities.map(getFacilityOption),
    [facilities],
  );

  const selectFacilityOption = useCallback(
    (option: FacilityOption | null) => {
      if (!contact || !account) {
        return;
      }
      const facility = facilitiesById[option?.value];
      dispatch(setFacility({ facility }));
      const shippingInformation = facility
        ? convertFacilityToShippingInformation(account, contact, facility)
        : null;
      dispatch(setShippingInformation(shippingInformation));
      if (!shippingInformation) {
        dispatch(setShippingType({ shippingType: undefined }));
      }
    },
    [facilitiesById, account, contact, dispatch],
  );

  const setOneTimeAddress = useCallback(
    (address: AddressInformation) => {
      dispatch(setShippingInformation(address));
      dispatch(setFacility({ facility: undefined }));
      toggleShowOneTimeAddressForm(false);
    },
    [dispatch, toggleShowOneTimeAddressForm],
  );

  const {
    confirmAddressState: {
      isSuggestedAddressModalOpen,
      isUnvalidatedAddressPopoverOpen,
      pendingAddress,
      suggestedAddresses,
    },
    clearPendingAddress,
    confirmAddress,
    openSuggestedAddressModal,
    openUnvalidatedAddressPopover,
  } = useConfirmAddressState({ onConfirmAddress: setOneTimeAddress });

  const oneTimeAddressFormikProps = useOneTimeAddressForm({
    initialAddress: oneTimeAddress,
    openSuggestedAddressModal,
    openUnvalidatedAddressPopover,
    setOneTimeAddress,
  });

  return (
    <AccordionItem>
      {({ isExpanded }) => (
        <>
          <AccordionButton>
            <Flex gap={1} overflow="hidden">
              <Box flexShrink={0}>Ship To Facility</Box>
              {!isExpanded ? (
                <InlinedAddressDisplay
                  color="feature.secondary"
                  spanProps={{ color: 'feature.secondary' }}
                  addressInformation={
                    shippingInformation
                      ? {
                          attention: shippingInformation.attention,
                          businessName:
                            shippingInformation.facilityName ??
                            shippingInformation.businessName,
                          address: shippingInformation.address,
                        }
                      : null
                  }
                />
              ) : null}
            </Flex>
          </AccordionButton>
          <AccordionPanel
            as={Flex}
            flexDirection="column"
            gap={1.5}
            align="stretch"
          >
            {useOneTimeAddress ? (
              <>
                {showOneTimeAddressForm ? (
                  <>
                    <OneTimeAddressForm
                      formikProps={oneTimeAddressFormikProps}
                    />
                    <SuggestedAddressModal
                      isOpen={isSuggestedAddressModalOpen}
                      onClose={clearPendingAddress}
                      originalAddress={pendingAddress}
                      suggestedAddresses={suggestedAddresses}
                      onConfirmAddress={confirmAddress}
                    />
                  </>
                ) : (
                  <Flex gap={2}>
                    <Box>
                      <AddressDisplay addressInformation={oneTimeAddress} />
                    </Box>
                    <Tooltip placement="bottom" label="Edit one-time address">
                      <IconButton
                        onClick={() => toggleShowOneTimeAddressForm(true)}
                      >
                        <PencilBarIcon />
                      </IconButton>
                    </Tooltip>
                  </Flex>
                )}
                <Flex justifyContent="space-between">
                  <Tooltip
                    placement="right"
                    label="Contact must be associated with an account to select a facility"
                    isDisabled={canSelectFacility}
                  >
                    <Button
                      size="md"
                      variant="link"
                      onClick={() => {
                        dispatch(setShippingInformation(null));
                        toggleUseOneTimeAddress(false);
                      }}
                      isDisabled={!canSelectFacility}
                    >
                      Select Facility
                    </Button>
                  </Tooltip>
                  {showOneTimeAddressForm && (
                    <Popover
                      isOpen={isUnvalidatedAddressPopoverOpen}
                      placement="top"
                    >
                      <PopoverAnchor>
                        <Button
                          size="md"
                          onClick={oneTimeAddressFormikProps.submitForm}
                          isDisabled={!oneTimeAddressFormikProps.isValid}
                          isLoading={oneTimeAddressFormikProps.isSubmitting}
                        >
                          Save
                        </Button>
                      </PopoverAnchor>
                      <PopoverContent>
                        <PopoverHeader>
                          We couldn&apos;t validate your address
                        </PopoverHeader>
                        <PopoverBody>
                          An invalid shipping address can cause delivery delays.
                        </PopoverBody>
                        <PopoverFooter>
                          <PopoverButtonGroup>
                            <Button
                              variant="secondary"
                              onClick={clearPendingAddress}
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={() => confirmAddress(pendingAddress)}
                            >
                              Proceed anyway
                            </Button>
                          </PopoverButtonGroup>
                        </PopoverFooter>
                      </PopoverContent>
                    </Popover>
                  )}
                </Flex>
              </>
            ) : account ? (
              <>
                <FormControl>
                  <FormLabel>Customer Facility</FormLabel>
                  <CreatableSelect
                    createLabelEntity="facility"
                    createOptionPosition="first"
                    options={facilityOptions}
                    value={facility ? getFacilityValue(facility) : null}
                    isClearable={true}
                    onCreateOption={() =>
                      dispatch(
                        openFacilityDrawer.request({
                          accountId: account.id,
                          callBack: ({ facility }) => {
                            dispatch(
                              selectNewlyAddedFacility.success({ facility }),
                            );
                            dispatch(
                              setShippingInformation(
                                convertFacilityToShippingInformation(
                                  account,
                                  contact,
                                  facility,
                                ),
                              ),
                            );
                          },
                          mode: FACILITY_DRAWER_MODES.CREATE,
                        }),
                      )
                    }
                    onChange={selectFacilityOption}
                    isValidNewOption={() => true}
                    getOptionValue={(option: FacilityOption) => {
                      if (option.value && facilitiesById[option.value]) {
                        const facility = facilitiesById[option.value];
                        return `${facility.name} ${facility.attention} ${
                          facility.address
                            ? formatAddressString(facility.address)
                            : ''
                        }`;
                      }
                      return option.value;
                    }}
                  />
                </FormControl>
                <Divider />
                <Button
                  size="md"
                  variant="link"
                  alignSelf="start"
                  onClick={() => {
                    dispatch(setShippingInformation(null));
                    dispatch(setFacility({ facility: undefined }));
                    toggleUseOneTimeAddress(true);
                    toggleShowOneTimeAddressForm(true);
                  }}
                >
                  Use one-time address
                </Button>
                <FacilityDrawer />
              </>
            ) : null}
          </AccordionPanel>
        </>
      )}
    </AccordionItem>
  );
};
